/**
 * Searches medical PA orders via the Postgres-backed backend endpoint.
 * @module searchForMedicalPaOrdersFromPostgres
 */

import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { getBearerToken } from "../../postCall/bearerToken";

/**
 * Parameters for searching and paginating medical PA orders from Postgres.
 *
 * @property org_id - Organization or facility ID that scopes the search
 * @property query - Free-text search query matched against indexed order fields
 * @property facet_filters - Active facet filter selections keyed by attribute name
 * @property hits_per_page - Page size for result hits
 * @property page - Zero-based page index
 * @property created_at_start - Optional lower bound for order creation date (ISO string)
 * @property created_at_end - Optional upper bound for order creation date (ISO string)
 * @property date_of_service_start - Optional lower bound for date of service (ISO string)
 * @property date_of_service_end - Optional upper bound for date of service (ISO string)
 * @property date_of_work_start - Optional lower bound for date of work (ISO string)
 * @property date_of_work_end - Optional upper bound for date of work (ISO string)
 * @property date_of_work_only - When true, restricts results to orders with a date_of_work value
 * @property timezone_offset - Client timezone offset in minutes; defaults to the browser offset
 * @property export - When true, requests an export-oriented response from the backend
 * @property sort_by - Optional canonical backend field name to sort results by (e.g. "first_name", "created_at_iso")
 * @property sort_dir - Optional sort direction; "asc" for ascending, "desc" for descending
 */
export interface SearchForMedicalPaOrdersFromPostgresParams {
  org_id: string;
  query: string;
  facet_filters: Record<string, string[]>;
  hits_per_page: number;
  page: number;
  created_at_start?: string;
  created_at_end?: string;
  date_of_service_start?: string;
  date_of_service_end?: string;
  date_of_work_start?: string;
  date_of_work_end?: string;
  date_of_work_only?: boolean;
  timezone_offset?: number;
  export?: boolean;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  default_sort?: Array<{
    field: string;
    dir: "asc" | "desc";
  }>;
}

/**
 * Searches for medical PA orders in Postgres with pagination, filtering, and date ranges.
 *
 * @param params - Search criteria including query, facet filters, pagination, and date bounds.
 * @returns Raw response text (JSON string) from the backend.
 * @throws Re-throws any network or parsing error after logging.
 */
export const searchForMedicalPaOrdersFromPostgres = async (
  params: SearchForMedicalPaOrdersFromPostgresParams,
): Promise<any> => {
  const bearerToken = await getBearerToken();

  const raw = JSON.stringify({
    org_id: params.org_id,
    query: params.query,
    facet_filters: params.facet_filters,
    hits_per_page: params.hits_per_page,
    page: params.page,
    created_at_start: params.created_at_start,
    created_at_end: params.created_at_end,
    date_of_service_start: params.date_of_service_start,
    date_of_service_end: params.date_of_service_end,
    date_of_work_start: params.date_of_work_start,
    date_of_work_end: params.date_of_work_end,
    ...(params.date_of_work_only !== undefined && {
      date_of_work_only: params.date_of_work_only,
    }),
    timezone_offset: params.timezone_offset ?? new Date().getTimezoneOffset(),
    ...(params.export !== undefined && { export: params.export }),
    ...(params.sort_by !== undefined && { sort_by: params.sort_by }),
    ...(params.sort_dir !== undefined && { sort_dir: params.sort_dir }),
    ...(params.default_sort !== undefined && {
      default_sort: params.default_sort,
    }),
  });

  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
    body: raw,
  };

  try {
    const response = await fetch(
      API_ENDPOINTS.SEARCH_FOR_MEDICAL_PA_ORDERS_FROM_POSTGRES,
      requestOptions,
    );
    const result = await response.text();
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
